import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { faBuilding, faClose, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Animate, { AnimationTypes } from "../../../../components/Animate/Animate"
import { ButtonIcon } from "../../../../components/Button/Button"
import { GenericDropDown } from "../../../../components/Dropdown/Dropdown"
import { PopupHelpButton } from "../../../../components/Help/PopupHelpButton"
import InputField from "../../../../components/InputField/InputField"
import ModalPortal from "../../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../../components/Titles/Titles"
import { SIZE } from "../../../../configuration/Sizes"
import THEME from "../../../../configuration/Theme"
import {
  type FiscalIssuerField,
  type FiscalIssuerValidationErrors,
  BRAZILIAN_STATES,
  validateFiscalIssuerSettings,
} from "../../../../Objects/FiscalIntegration/validateFiscalIssuerSettings"
import { type ISaveFiscalIssuerSettings } from "../../../../Objects/FiscalIntegration/IFiscalIssuerSettings"
import defaultStyle from "../../../../styles/defaultPageStyle.module.css"
import {
  CNPJ_FORMATTED_LENGTH,
  formatCnpj,
  formatZipCode,
  normalizeCnpj,
  normalizeZipCode,
} from "../../../../utils/documentFormat"

type Props = {
  isOpen: boolean
  issuer: ISaveFiscalIssuerSettings
  onChange: (issuer: ISaveFiscalIssuerSettings) => void
  onClose: () => void
  onSave: () => void
}

export const FiscalIssuerSettingsPopup: React.FC<Props> = ({ isOpen, issuer, onChange, onClose, onSave }) => {
  const { t } = useTranslation(["fiscalIntegration", "buttons", "helpPopup"])
  const [validationVisible, setValidationVisible] = useState(false)

  useEffect(() => {
    setValidationVisible(false)
  }, [isOpen])

  const fieldErrors = useMemo<FiscalIssuerValidationErrors>(
    () => (validationVisible ? validateFiscalIssuerSettings(issuer) : {}),
    [issuer, validationVisible],
  )

  if (!isOpen) {
    return null
  }

  const patch = (next: Partial<ISaveFiscalIssuerSettings>) => onChange({ ...issuer, ...next })

  const fieldId = (field: FiscalIssuerField) => `fiscal-issuer-${field}`

  const getError = (field: FiscalIssuerField) => {
    const messageKey = fieldErrors[field]
    return messageKey ? t(messageKey) : undefined
  }

  const handleSave = () => {
    const errors = validateFiscalIssuerSettings(issuer)

    const firstInvalidField = Object.keys(errors)[0] as FiscalIssuerField | undefined

    if (firstInvalidField) {
      setValidationVisible(true)
      window.requestAnimationFrame(() => document.getElementById(fieldId(firstInvalidField))?.focus())
      return
    }

    setValidationVisible(false)
    onSave()
  }

  const errorCount = Object.keys(fieldErrors).length

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              icon={faBuilding}
              iconColor={THEME.colors.iconBGColor}
              title={t("issuer.title")}
            >
              <div className={`${defaultStyle.ButtonsActions} gap-1`}>
                <PopupHelpButton
                  variant="global"
                  helpKey="pages.mystore.fiscalintegration.issuer.1"
                  title={t("helpPopup:global.fiscalIssuerSettings.title")}
                  description={t("helpPopup:global.fiscalIssuerSettings.description")}
                  steps={t("helpPopup:global.fiscalIssuerSettings.steps", { returnObjects: true }) as string[]}
                  bullets={t("helpPopup:global.fiscalIssuerSettings.bullets", { returnObjects: true }) as string[]}
                />

                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalIssuerSettingsPopup.button.onClose"
                />
              </div>
            </TitlePageResponsiveIconSmall>

            <div className="p-2">
              <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
                <section className="mb-3 rounded-xl border-2 border-sky-100 bg-sky-50 p-3 text-xs leading-relaxed text-sky-900">
                  <strong className="block text-sm">{t("issuer.guideTitle")}</strong>

                  <p className="mt-1">{t("issuer.description")}</p>
                </section>
              </Animate>

              {validationVisible && errorCount > 0 && (
                <section
                  className="mb-3 flex gap-3 rounded-xl border-2 border-red-300 bg-red-50 p-3 text-red-900"
                  role="alert"
                  aria-live="assertive"
                >
                  <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 shrink-0" />

                  <div>
                    <strong className="block text-sm">{t("validation.summary", { count: errorCount })}</strong>
                    <p className="mt-1 text-xs">{t("issuer.validation.summaryDescription")}</p>
                  </div>
                </section>
              )}

              <section className="p-3 space-y-4">
                <GenericDropDown
                  id={fieldId("state")}
                  legend={t("issuer.fields.state")}
                  initialState={issuer.state}
                  data={[
                    { value: "", label: t("issuer.fields.statePlaceholder") },
                    ...BRAZILIAN_STATES.map((state) => ({
                      value: state,
                      label: t(`issuer.states.${state}`),
                    })),
                  ]}
                  errorMessage={getError("state")}
                  handlerFuncion={(event) => patch({ state: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalIssuerSettingsPopup.select.state"
                />

                <InputField
                  id={fieldId("cnpj")}
                  type="text"
                  value={formatCnpj(issuer.cnpj)}
                  placeholder={t("issuer.fields.cnpj")}
                  maxLength={CNPJ_FORMATTED_LENGTH}
                  required
                  inputMode="numeric"
                  errorMessage={getError("cnpj")}
                  onChange={(event) => patch({ cnpj: normalizeCnpj(event.target.value) })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalIssuerSettingsPopup.input.cnpj"
                />

                <InputField
                  id={fieldId("stateRegistration")}
                  type="text"
                  value={issuer.stateRegistration}
                  placeholder={t("issuer.fields.stateRegistration")}
                  maxLength={14}
                  required
                  inputMode="numeric"
                  errorMessage={getError("stateRegistration")}
                  onChange={(event) => patch({ stateRegistration: event.target.value.replace(/\D/g, "").slice(0, 14) })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalIssuerSettingsPopup.input.stateRegistration"
                />

                <InputField
                  id={fieldId("legalName")}
                  type="text"
                  value={issuer.legalName}
                  placeholder={t("issuer.fields.legalName")}
                  maxLength={60}
                  required
                  errorMessage={getError("legalName")}
                  onChange={(event) => patch({ legalName: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalIssuerSettingsPopup.input.legalName"
                />

                <InputField
                  id={fieldId("tradeName")}
                  type="text"
                  value={issuer.tradeName}
                  placeholder={t("issuer.fields.tradeName")}
                  maxLength={60}
                  errorMessage={getError("tradeName")}
                  onChange={(event) => patch({ tradeName: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalIssuerSettingsPopup.input.tradeName"
                />

                <InputField
                  id={fieldId("street")}
                  type="text"
                  value={issuer.street}
                  placeholder={t("issuer.fields.street")}
                  maxLength={60}
                  required
                  errorMessage={getError("street")}
                  onChange={(event) => patch({ street: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalIssuerSettingsPopup.input.street"
                />

                <InputField
                  id={fieldId("number")}
                  type="text"
                  value={issuer.number}
                  placeholder={t("issuer.fields.number")}
                  maxLength={60}
                  required
                  errorMessage={getError("number")}
                  onChange={(event) => patch({ number: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalIssuerSettingsPopup.input.number"
                />

                <InputField
                  id={fieldId("complement")}
                  type="text"
                  value={issuer.complement}
                  placeholder={t("issuer.fields.complement")}
                  maxLength={60}
                  errorMessage={getError("complement")}
                  onChange={(event) => patch({ complement: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalIssuerSettingsPopup.input.complement"
                />

                <InputField
                  id={fieldId("neighborhood")}
                  type="text"
                  value={issuer.neighborhood}
                  placeholder={t("issuer.fields.neighborhood")}
                  maxLength={60}
                  required
                  errorMessage={getError("neighborhood")}
                  onChange={(event) => patch({ neighborhood: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalIssuerSettingsPopup.input.neighborhood"
                />

                <InputField
                  id={fieldId("city")}
                  type="text"
                  value={issuer.city}
                  placeholder={t("issuer.fields.city")}
                  maxLength={60}
                  required
                  errorMessage={getError("city")}
                  onChange={(event) => patch({ city: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalIssuerSettingsPopup.input.city"
                />

                <InputField
                  id={fieldId("zipCode")}
                  type="text"
                  value={formatZipCode(issuer.zipCode)}
                  placeholder={t("issuer.fields.zipCode")}
                  maxLength={9}
                  required
                  inputMode="numeric"
                  errorMessage={getError("zipCode")}
                  onChange={(event) => patch({ zipCode: normalizeZipCode(event.target.value) })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalIssuerSettingsPopup.input.zipCode"
                />
              </section>

              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={handleSave}
                  className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700"
                  data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalIssuerSettingsPopup.button.handleSave"
                >
                  {t("buttons:button_save")}
                </button>
              </div>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
